import {useState} from 'react';
import axios, {sessionAxios} from '@lib/axios';
import {AxiosResponse, Method} from 'axios';
import {APIRequestConfig} from '@enums/request-config';
import {useRouter} from 'next/router';
import {LocaleEnum} from '@enums/locale';
import {normalizeApiLocale} from '@lib/locale';

interface UseRequestInterface<D> {
  url: string;
  method: Method;
  body?: D;
  requiresAuth?: boolean;
  params?: {[key: string]: unknown};
  headers?: {[key: string]: unknown};
  onSuccess?: (data: any) => void;
  onError?: (
    errors: {message: string; field?: string}[],
    statusCode: number
  ) => void;
}
export const useRequest = (locale?: string) => {
  if (!locale) {
    const router = useRouter();
    locale = router.locale || LocaleEnum.FI;
  }
  const [errors, setErrors] = useState<{message: string; field?: string}[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const sendRequest = async <T = any, D = any>({
    url,
    method,
    body,
    params,
    onSuccess,
    onError,
    requiresAuth,
    headers,
  }: UseRequestInterface<D>) => {
    try {
      setErrors([]);
      setLoading(true);
      const response = await (requiresAuth ? sessionAxios : axios).request<
        T,
        AxiosResponse<T>,
        D
      >({
        url,
        method,
        data: body,
        params: params,
        headers: {
          [APIRequestConfig.X_LOCALE]: normalizeApiLocale(locale),
          ...headers,
        },
      });
      if (onSuccess) {
        return onSuccess(response.data);
      }
      return response.data;
    } catch (err: any) {
      const statusCode = err.response?.status || 500;
      let formattedErrors: {message: string; field?: string}[];
      const responseData = err.response?.data;

      if (typeof responseData === 'string') {
        formattedErrors = [{message: responseData}];
        setErrors(formattedErrors);
      } else if (responseData?.message || responseData?.error) {
        formattedErrors = [
          {message: responseData.message || responseData.error},
        ];
        setErrors(formattedErrors);
      } else if (responseData?.errors) {
        formattedErrors = Object.keys(responseData.errors).map(key => {
          const error = responseData.errors[key];
          return {
            message: Array.isArray(error) ? error[0] : error,
            field: key,
          };
        });
        setErrors(formattedErrors);
      } else {
        formattedErrors = [
          {
            message:
              err.response?.data?.message ||
              err.response?.data?.error ||
              'Unknown request error!',
          },
        ];
        setErrors(formattedErrors);
      }
      if (onError) onError(formattedErrors, statusCode);
    } finally {
      setLoading(false);
    }
  };
  return {sendRequest, errors, loading};
};
